import { Link, useLocation } from 'react-router-dom'
import { cn } from '../../lib/utils'
import {
  BarChart3,
  Package,
  Users,
  Activity,
  Settings,
  ChevronLeft,
  ChevronRight,
  Server,
} from 'lucide-react'
import { Button } from '../ui'

interface SidebarProps {
  isCollapsed: boolean
  onToggle: () => void
  userRole?: 'admin' | 'user'
}

const navigation = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: BarChart3,
    description: 'Overview and analytics',
    gradient: 'from-blue-500 to-blue-600',
    adminOnly: true,
  },
  {
    name: 'Repositories',
    href: '/repositories',
    icon: Package,
    description: 'Manage Docker repositories',
    gradient: 'from-green-500 to-green-600',
  },
  {
    name: 'Deployments',
    href: '/deployments',
    icon: Server,
    description: 'Container deployments',
    gradient: 'from-cyan-500 to-cyan-600',
  },
  {
    name: 'Users',
    href: '/users',
    icon: Users,
    description: 'User management',
    gradient: 'from-purple-500 to-purple-600',
    adminOnly: true,
  },
  {
    name: 'Activity',
    href: '/activity',
    icon: Activity,
    description: 'System activity logs',
    gradient: 'from-orange-500 to-orange-600',
    adminOnly: true,
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings,
    description: 'Application settings',
    gradient: 'from-gray-500 to-gray-600',
  },
]

export function Sidebar({ isCollapsed, onToggle, userRole }: SidebarProps) {
  const location = useLocation()
  const filteredNavigation =
    userRole === 'admin' ? navigation : navigation.filter((item) => !item.adminOnly)

  return (
    <div
      className={cn(
        'sticky top-0 flex h-screen flex-col bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 shadow-2xl transition-all duration-300 ease-out',
        'before:pointer-events-none before:absolute before:inset-0 before:bg-gradient-to-b before:from-blue-600/5 before:to-purple-600/5',
        isCollapsed ? 'w-16' : 'w-72'
      )}
    >
      {/* Header */}
      <div
        className={cn(
          'relative flex min-h-[88px] items-center justify-center border-b border-slate-700/50 p-6',
          'bg-gradient-to-r from-slate-800/50 to-slate-700/50 backdrop-blur-sm'
        )}
      >
        {/* Only show logo when not collapsed */}
        {!isCollapsed && (
          <div className="flex min-w-0 flex-1 items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-400 to-purple-500 opacity-75 blur-sm"></div>
              <div className="relative rounded-xl bg-white p-2 shadow-lg">
                <img
                  src="/danone-logo.svg"
                  alt="Danone"
                  className={cn('w-auto', 'h-8')}
                  onError={(e) => {
                    ;(e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              </div>
            </div>
          </div>
        )}
        <div className="flex h-full w-16 items-center justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className={cn(
              'h-10 w-10 flex-shrink-0 justify-center rounded-xl p-0 transition-all duration-300',
              'border border-slate-600/50 bg-slate-800/50 hover:bg-slate-700/80',
              'hover:scale-110 hover:shadow-lg hover:shadow-blue-500/20',
              'text-slate-300 hover:text-white',
              isCollapsed ? 'ml-0' : 'ml-3'
            )}
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <ChevronLeft className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-4">
        <div className="space-y-2">
          {filteredNavigation.map((item) => {
            const isActive = location.pathname.startsWith(item.href)
            const Icon = item.icon

            return (
              <div key={item.name} className="group relative">
                <Link
                  to={item.href}
                  className={cn(
                    'relative flex items-center overflow-hidden rounded-lg text-sm font-semibold transition-all duration-300',
                    'w-full transform hover:scale-[1.01] active:scale-[0.99]',
                    isActive
                      ? 'bg-gradient-to-r from-blue-600/90 to-purple-600/90 text-white shadow-lg shadow-blue-500/25'
                      : 'text-slate-300 hover:bg-slate-800/80 hover:text-white',
                    isCollapsed ? 'justify-center px-2 py-3' : 'space-x-4 px-4 py-3'
                  )}
                  title={isCollapsed ? item.description : undefined}
                >
                  {/* Background gradient for active state */}
                  {isActive && (
                    <div
                      className={cn(
                        'absolute inset-0 rounded-xl bg-gradient-to-r opacity-20',
                        item.gradient
                      )}
                    />
                  )}

                  {/* Icon container */}
                  <div
                    className={cn(
                      'relative flex items-center justify-center rounded-lg transition-all duration-300',
                      'group-hover:scale-110',
                      isActive ? 'text-white' : 'text-slate-400 group-hover:text-white',
                      isCollapsed ? 'h-6 w-6' : 'h-8 w-8'
                    )}
                  >
                    {isActive && (
                      <div
                        className={cn(
                          'absolute inset-0 rounded-lg bg-gradient-to-br opacity-60 blur-sm',
                          item.gradient
                        )}
                      />
                    )}
                    <Icon
                      className={cn(
                        'relative z-10 transition-all duration-300',
                        isCollapsed ? 'h-5 w-5' : 'h-6 w-6'
                      )}
                    />
                  </div>

                  {!isCollapsed && (
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-semibold">{item.name}</span>
                      <span
                        className={cn(
                          'truncate text-xs transition-colors duration-300',
                          isActive ? 'text-blue-100' : 'text-slate-500 group-hover:text-slate-300'
                        )}
                      >
                        {item.description}
                      </span>
                    </div>
                  )}

                  {/* Active indicator line */}
                  {isActive && (
                    <div className="absolute right-0 top-1/2 h-8 w-1 -translate-y-1/2 transform rounded-l-full bg-gradient-to-b from-blue-300 to-purple-300 shadow-lg shadow-blue-400/50" />
                  )}

                  {/* Hover glow effect */}
                  <div
                    className={cn(
                      'absolute inset-0 !m-0 rounded-xl bg-gradient-to-r from-blue-500/0 to-purple-500/0 opacity-0 transition-all duration-300',
                      'group-hover:from-blue-500/10 group-hover:to-purple-500/10 group-hover:opacity-100'
                    )}
                  />
                </Link>

                {/* Tooltip for collapsed state */}
                {isCollapsed && (
                  <div
                    className={cn(
                      'absolute left-full top-1/2 ml-3 -translate-y-1/2 transform px-3 py-2',
                      'z-50 rounded-lg bg-slate-800 text-sm text-white shadow-xl',
                      'opacity-0 transition-all duration-300 group-hover:opacity-100',
                      'pointer-events-none border border-slate-600/50',
                      'before:absolute before:right-full before:top-1/2 before:-translate-y-1/2 before:transform',
                      'before:border-4 before:border-transparent before:border-r-slate-800'
                    )}
                  >
                    <div className="font-semibold">{item.name}</div>
                    <div className="text-xs text-slate-300">{item.description}</div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </nav>

      {/* Footer */}
      {!isCollapsed && (
        <div className="border-t border-slate-700/50 bg-gradient-to-r from-slate-800/30 to-slate-700/30 p-4 backdrop-blur-sm">
          <div className="rounded-xl border border-slate-600/30 bg-slate-800/50 p-4">
            <div className="text-xs text-slate-300">
              <p className="mb-1 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text font-bold text-transparent text-white">
                Danone Model Registry
              </p>
              <p className="text-slate-400">Version 1.0.0</p>
              <div className="mt-2 flex items-center space-x-1">
                <div className="h-2 w-2 animate-pulse rounded-full bg-green-400"></div>
                <span className="font-medium text-green-400">Online</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subtle animation line */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 opacity-30"></div>
    </div>
  )
}
